import { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { formatTRY } from '@/lib/currencyFormat';
import * as LoanDB from '@/lib/personalLoansDb';
import NewLoanModal from './components/NewLoanModal';

function getStatusStyle(status: string) {
  const map: Record<string, string> = {
    'Aktif': 'bg-amber-100 text-amber-800',
    'Kısmi Ödendi': 'bg-sky-100 text-sky-800',
    'Ödendi': 'bg-emerald-100 text-emerald-800',
    'Gecikti': 'bg-red-100 text-red-800',
    'İptal': 'bg-foreground-100 text-foreground-500',
    'Hukuki Takip': 'bg-rose-100 text-rose-800',
  };
  return map[status] || 'bg-foreground-100 text-foreground-500';
}

function getBorrowerTypeLabel(type: string) {
  const map: Record<string, string> = {
    'customer': 'Müşteri',
    'employee': 'Personel',
    'supplier': 'Tedarikçi',
    'other': 'Diğer',
  };
  return map[type] || type;
}

export default function PersonalLoansPage() {
  const navigate = useNavigate();
  const [loans, setLoans] = useState<LoanDB.PersonalLoan[]>([]);
  const [summary, setSummary] = useState<LoanDB.PersonalLoanSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showNewModal, setShowNewModal] = useState(false);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [typeFilter, setTypeFilter] = useState('all');
  const [sortBy, setSortBy] = useState('newest');

  const loadData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [loansRes, summaryRes] = await Promise.all([
        LoanDB.getPersonalLoans(),
        LoanDB.getPersonalLoanSummary(),
      ]);
      if (loansRes.error) setError(loansRes.error);
      setLoans(loansRes.data || []);
      setSummary(summaryRes.data);
    } catch {
      setError('Veri yüklenirken hata oluştu.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  const filteredLoans = useMemo(() => {
    let result = [...loans];

    if (search.trim()) {
      const q = search.toLowerCase();
      result = result.filter(l =>
        l.borrower_name.toLowerCase().includes(q) ||
        l.loan_no.toLowerCase().includes(q) ||
        (l.borrower_phone && l.borrower_phone.includes(q))
      );
    }

    if (statusFilter !== 'all') {
      result = result.filter(l => l.status === statusFilter);
    }

    if (typeFilter !== 'all') {
      result = result.filter(l => l.borrower_type === typeFilter);
    }

    switch (sortBy) {
      case 'oldest':
        result.sort((a, b) => a.loan_date.localeCompare(b.loan_date));
        break;
      case 'highest_remaining':
        result.sort((a, b) => b.remaining_amount - a.remaining_amount);
        break;
      case 'nearest_due':
        result.sort((a, b) => (a.due_date || '9999') > (b.due_date || '9999') ? 1 : -1);
        break;
      case 'most_overdue':
        result.sort((a, b) => (a.due_date || '0000') > (b.due_date || '0000') ? -1 : 1);
        break;
      default:
        break;
    }

    return result;
  }, [loans, search, statusFilter, typeFilter, sortBy]);

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center h-[60vh]">
        <div className="w-12 h-12 rounded-full border-2 border-primary-200 border-t-primary-500 animate-spin mb-4"></div>
        <h2 className="text-lg font-semibold text-foreground-700">Yükleniyor...</h2>
      </div>
    );
  }

  return (
    <div className="space-y-6 md:space-y-8">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h2 className="text-xl md:text-2xl font-bold text-foreground-900 font-heading">Elden Verilen Borç Takibi</h2>
          <p className="text-sm text-foreground-400 mt-0.5">Personel, müşteri ve diğer kişilere verilen borçların takibi</p>
        </div>
        <button
          onClick={() => setShowNewModal(true)}
          className="btn-primary whitespace-nowrap flex items-center gap-2 cursor-pointer"
        >
          <i className="ri-add-line"></i>
          <span>Yeni Borç Ver</span>
        </button>
      </div>

      {/* Summary Cards */}
      {summary && (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-7 gap-3">
          <div className="stat-card p-4 text-center">
            <p className="text-xs text-foreground-400 mb-1">Aktif Borç</p>
            <p className="text-xl font-bold text-foreground-900">{summary.active_count}</p>
          </div>
          <div className="stat-card p-4 text-center">
            <p className="text-xs text-foreground-400 mb-1">Toplam Verilen</p>
            <p className="text-xl font-bold text-foreground-900">{formatTRY(summary.total_given)}</p>
          </div>
          <div className="stat-card p-4 text-center">
            <p className="text-xs text-foreground-400 mb-1">Toplam Geri Alınan</p>
            <p className="text-xl font-bold text-emerald-600">{formatTRY(summary.total_repaid)}</p>
          </div>
          <div className="stat-card p-4 text-center">
            <p className="text-xs text-foreground-400 mb-1">Kalan Alacak</p>
            <p className="text-xl font-bold text-amber-600">{formatTRY(summary.total_remaining)}</p>
          </div>
          <div className="stat-card p-4 text-center">
            <p className="text-xs text-foreground-400 mb-1">Vadesi Geçen</p>
            <p className="text-xl font-bold text-red-600">{summary.overdue_count} <span className="text-sm font-normal">({formatTRY(summary.overdue_amount)})</span></p>
          </div>
          <div className="stat-card p-4 text-center">
            <p className="text-xs text-foreground-400 mb-1">Bu Ay Verilen</p>
            <p className="text-xl font-bold text-foreground-900">{formatTRY(summary.this_month_given)}</p>
          </div>
          <div className="stat-card p-4 text-center">
            <p className="text-xs text-foreground-400 mb-1">Bu Ay Tahsil Edilen</p>
            <p className="text-xl font-bold text-emerald-600">{formatTRY(summary.this_month_repaid)}</p>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-foreground-400 text-sm"></i>
          <input
            type="text"
            placeholder="Kişi, telefon veya borç no ile ara..."
            value={search}
            onChange={e => setSearch(e.target.value)}
            className="w-full pl-9 pr-4 py-2.5 text-sm bg-background-50 border border-background-200/70 rounded-lg focus:outline-none focus:border-primary-300 text-foreground-700"
          />
        </div>
        <select
          value={statusFilter}
          onChange={e => setStatusFilter(e.target.value)}
          className="px-4 py-2.5 text-sm bg-background-50 border border-background-200/70 rounded-lg focus:outline-none focus:border-primary-300 text-foreground-700 cursor-pointer"
        >
          <option value="all">Tüm Durumlar</option>
          <option value="Aktif">Aktif</option>
          <option value="Kısmi Ödendi">Kısmi Ödendi</option>
          <option value="Ödendi">Ödendi</option>
          <option value="Gecikti">Gecikti</option>
          <option value="Hukuki Takip">Hukuki Takip</option>
          <option value="İptal">İptal</option>
        </select>
        <select
          value={typeFilter}
          onChange={e => setTypeFilter(e.target.value)}
          className="px-4 py-2.5 text-sm bg-background-50 border border-background-200/70 rounded-lg focus:outline-none focus:border-primary-300 text-foreground-700 cursor-pointer"
        >
          <option value="all">Tüm Kişi Türleri</option>
          <option value="customer">Müşteri</option>
          <option value="employee">Personel</option>
          <option value="supplier">Tedarikçi</option>
          <option value="other">Diğer</option>
        </select>
        <select
          value={sortBy}
          onChange={e => setSortBy(e.target.value)}
          className="px-4 py-2.5 text-sm bg-background-50 border border-background-200/70 rounded-lg focus:outline-none focus:border-primary-300 text-foreground-700 cursor-pointer"
        >
          <option value="newest">En Yeni</option>
          <option value="oldest">En Eski</option>
          <option value="highest_remaining">En Yüksek Kalan</option>
          <option value="nearest_due">En Yakın Vade</option>
          <option value="most_overdue">En Gecikmiş</option>
        </select>
      </div>

      {/* Error */}
      {error && (
        <div className="p-4 rounded-xl bg-red-50 border border-red-200 flex items-center gap-3">
          <i className="ri-error-warning-line text-red-400 text-lg"></i>
          <p className="text-sm text-red-700">{error}</p>
          <button onClick={loadData} className="ml-auto btn-primary text-xs cursor-pointer">Tekrar Dene</button>
        </div>
      )}

      {/* Loans Table - Desktop */}
      <div className="hidden lg:block stat-card overflow-x-auto">
        <table className="w-full min-w-[900px]">
          <thead>
            <tr className="border-b border-background-200">
              <th className="table-header">Borç No</th>
              <th className="table-header">Kişi</th>
              <th className="table-header">Tür</th>
              <th className="table-header">Telefon</th>
              <th className="table-header">Verilen</th>
              <th className="table-header">Geri Alınan</th>
              <th className="table-header">Kalan</th>
              <th className="table-header">Tarih</th>
              <th className="table-header">Vade</th>
              <th className="table-header">Durum</th>
            </tr>
          </thead>
          <tbody>
            {filteredLoans.map(loan => (
              <tr
                key={loan.id}
                onClick={() => navigate(`/personal-loans/${loan.id}`)}
                className="border-b border-background-100 hover:bg-background-50 transition-colors cursor-pointer"
              >
                <td className="table-cell font-medium text-primary-600 whitespace-nowrap">{loan.loan_no}</td>
                <td className="table-cell font-medium">{loan.borrower_name}</td>
                <td className="table-cell text-foreground-500 text-xs">{getBorrowerTypeLabel(loan.borrower_type)}</td>
                <td className="table-cell text-foreground-500 text-xs">{loan.borrower_phone || '—'}</td>
                <td className="table-cell font-medium">{formatTRY(loan.principal_amount)}</td>
                <td className="table-cell text-emerald-600">{formatTRY(loan.paid_amount)}</td>
                <td className="table-cell font-bold text-amber-600">{formatTRY(loan.remaining_amount)}</td>
                <td className="table-cell text-xs text-foreground-500">{loan.loan_date}</td>
                <td className="table-cell text-xs">{loan.due_date || '—'}</td>
                <td className="table-cell py-2">
                  <span className={`text-xs px-2 py-0.5 rounded-full font-medium whitespace-nowrap ${getStatusStyle(loan.status)}`}>
                    {loan.status}
                  </span>
                </td>
              </tr>
            ))}
            {filteredLoans.length === 0 && (
              <tr>
                <td colSpan={10} className="px-4 py-10 text-center text-sm text-foreground-400">
                  {search || statusFilter !== 'all' || typeFilter !== 'all'
                    ? 'Filtrelere uygun kayıt bulunamadı.'
                    : 'Henüz borç kaydı yok. "Yeni Borç Ver" butonuna tıklayarak ilk kaydı oluşturabilirsiniz.'}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Loans Cards - Mobile */}
      <div className="lg:hidden space-y-3">
        {filteredLoans.map(loan => (
          <div
            key={loan.id}
            onClick={() => navigate(`/personal-loans/${loan.id}`)}
            className="stat-card p-4 cursor-pointer space-y-2"
          >
            <div className="flex items-center justify-between">
              <span className="text-sm font-semibold text-primary-600">{loan.loan_no}</span>
              <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${getStatusStyle(loan.status)}`}>{loan.status}</span>
            </div>
            <div>
              <p className="text-sm font-medium text-foreground-800">{loan.borrower_name}</p>
              <p className="text-xs text-foreground-400">{getBorrowerTypeLabel(loan.borrower_type)}{loan.borrower_phone ? ` · ${loan.borrower_phone}` : ''}</p>
            </div>
            <div className="flex items-center justify-between text-xs">
              <span className="text-foreground-500">Verilen: {formatTRY(loan.principal_amount)}</span>
              <span className="text-emerald-600">Geri: {formatTRY(loan.paid_amount)}</span>
              <span className="font-bold text-amber-600">Kalan: {formatTRY(loan.remaining_amount)}</span>
            </div>
            <div className="flex items-center gap-2 text-xs text-foreground-400">
              <span>{loan.loan_date}</span>
              {loan.due_date && <span>· Vade: {loan.due_date}</span>}
            </div>
          </div>
        ))}
        {filteredLoans.length === 0 && (
          <p className="text-center py-8 text-sm text-foreground-400">
            {search || statusFilter !== 'all' || typeFilter !== 'all'
              ? 'Filtrelere uygun kayıt bulunamadı.'
              : 'Henüz borç kaydı yok.'}
          </p>
        )}
      </div>

      {/* New Loan Modal */}
      {showNewModal && (
        <NewLoanModal
          onClose={() => setShowNewModal(false)}
          onSuccess={() => { setShowNewModal(false); loadData(); }}
        />
      )}
    </div>
  );
}